import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Check, MapPin, Package } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { isOccupiable } from '@/lib/storageTree'
import { useAddLot } from '@/hooks/useInventoryMaster'
import { useStorageConditions, useStorageRoots, useFreeSlots } from '@/hooks/useStorage'
import {
  conditionLabel,
  describeNarrowing,
  partitionByCondition,
} from '@/lib/storageEligibility'
import { ConditionBadge } from '@/components/inventory/storage/ConditionControls'
import { matchingVendorLots } from '@/lib/vendorLot'
import type { ApiLot } from '@/types/inventory'
import type { ApiStorageNode, StorageCondition } from '@/types/storage'
import { useLocationText } from '@/hooks/useStoragePlaces'

interface AddLotModalProps {
  materialId: string | null
  materialLabel?: string
  /** What this material must be kept at, when the master data states one. */
  requiresCondition?: StorageCondition | null
  /**
   * Lots this material already holds, when the caller has them.
   *
   * Only used to warn that a vendor lot is a repeat — the receipt itself is
   * unaffected. Callers that do not hold the material's lots (the storage map,
   * the put-away panel) simply pass nothing and get today's behaviour; the
   * server still numbers the receipt correctly either way.
   */
  existingLots?: ApiLot[]
  /**
   * Receive straight into a slot the caller already chose.
   *
   * Set when this is opened from a slot on the map: the destination is not a
   * question there, and asking it again invites picking a different one by
   * accident. Omitted, the unit/slot pickers appear as usual.
   */
  slot?: ApiStorageNode | null
  open: boolean
  onClose: () => void
  /**
   * Fired only when a lot was actually saved, before `onClose`.
   *
   * Separate from `onClose`, which also fires on Cancel — a caller that tears
   * itself down after a receipt must not do so when nothing was received.
   */
  onSaved?: () => void
}

/**
 * Receive a lot: what arrived, and where it went.
 *
 * Two questions in that order, because that is the order they are answered on
 * the floor. Location is a real slot in the storage hierarchy rather than the
 * old zone-plus-typed-bin pair: a typed code cannot be checked against anything,
 * which is what produced the unresolved-location queue and made storage
 * conditions unverifiable.
 *
 * "Not put away yet" is a first-class answer. Stock that has been received but
 * not shelved is a real state, and forcing a location here would only teach
 * people to pick a wrong one.
 */
export function AddLotModal({
  materialId,
  materialLabel,
  requiresCondition = null,
  existingLots,
  slot = null,
  open,
  onClose,
  onSaved,
}: AddLotModalProps) {
  const locate = useLocationText()
  const addLot = useAddLot()
  const roots = useStorageRoots()
  // Labels, temperatures and — the part the rule needs — which conditions are
  // somewhere stock actually rests. Served, not hardcoded, so this and the
  // server's copy of the enum cannot drift apart.
  const conditions = useStorageConditions()

  const [vendorLotNumber, setVendorLotNumber] = useState('')
  const [quantity, setQuantity] = useState('')
  const [expiry, setExpiry] = useState('')
  const [unitId, setUnitId] = useState('')
  const [slotId, setSlotId] = useState('')
  const [error, setError] = useState<string | null>(null)
  /**
   * Receipts already on file for the vendor lot being entered, held while the
   * operator answers the confirmation. Null means nothing is being asked.
   */
  const [repeatOf, setRepeatOf] = useState<ApiLot[] | null>(null)

  useEffect(() => {
    if (open) {
      setVendorLotNumber('')
      setQuantity('')
      setExpiry('')
      setUnitId('')
      setSlotId('')
      setError(null)
      setRepeatOf(null)
    }
  }, [open])

  // Declared slots included (z8nrz7fqfy): the subtree listed stored rows only,
  // so a rack whose slots were never written out read as "full".
  const free = useFreeSlots(unitId || null)
  const freeTotal = free.data?.freeTotal ?? 0

  // Only slots that can actually take stock: free, unblocked, and never the
  // holding area, which is where unparseable legacy locations were parked.
  const freeSlots = useMemo(
    () =>
      (free.data?.nodes ?? []).filter(
        (n) => isOccupiable(n.kind) && !n.blocked && !n.isHoldingArea && !n.occupied,
      ),
    [free.data],
  )

  /**
   * Only units that can legally hold this material.
   *
   * The server refuses a mismatched put-away, so listing every unit offered a
   * choice guaranteed to fail — after the form was filled in. The ones kept out
   * are counted and explained rather than silently dropped: a picker showing
   * three of a facility's nine freezers otherwise reads as broken data.
   */
  // Memoised because `?? []` mints a fresh array on every render while the
  // reference is loading, which would make the partition below recompute for
  // nothing on each one.
  const conditionMeta = useMemo(() => conditions.data?.conditions ?? [], [conditions.data])
  const { eligible: units, hidden: hiddenUnits } = useMemo(
    () => partitionByCondition(roots.data ?? [], requiresCondition, conditionMeta),
    [roots.data, requiresCondition, conditionMeta],
  )
  const narrowing = describeNarrowing(requiresCondition, hiddenUnits.length, conditionMeta)
  const chosenUnit = units.find((u) => u.id === unitId) ?? null
  const qtyValid = quantity !== '' && !Number.isNaN(Number(quantity)) && Number(quantity) > 0

  /**
   * Ask first if this supplier lot is already here.
   *
   * Receiving the same vendor lot again does not open a new lot — the server
   * keeps that lot's base and gives the receipt the next number — and that is
   * the one thing about the save the operator cannot see from this form. It is
   * also the last moment a mis-keyed vendor number, or stock entered twice, is
   * cheap to stop.
   */
  function submit() {
    if (!materialId) return
    if (!qtyValid) {
      setError('Enter how many arrived.')
      return
    }
    // The expiry is on the container being received. Stock recorded without one
    // cannot be reasoned about by any FEFO pick, expiry filter or shelf-life
    // check, and nothing downstream can recover the date later.
    if (!expiry) {
      setError('Enter the expiry date printed on the container.')
      return
    }
    const repeats = matchingVendorLots(existingLots, vendorLotNumber)
    if (repeats.length > 0) {
      setRepeatOf(repeats)
      return
    }
    save()
  }

  function save() {
    if (!materialId) return
    addLot.mutate(
      {
        materialId,
        payload: {
          // Omitted when blank rather than sent as '' — an empty string is not
          // the same claim as "the container carries no number".
          vendorLotNumber: vendorLotNumber.trim() || undefined,
          quantity: Number(quantity),
          expiryDate: expiry,
          storageNodeId: slot ? slot.id : slotId || undefined,
        },
      },
      {
        onSuccess: () => {
          onSaved?.()
          onClose()
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'Could not add the lot.'),
      },
    )
  }

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader className='space-y-1'>
          <DialogTitle className='text-lg'>Receive stock</DialogTitle>
          {materialLabel && (
            <p className='flex flex-wrap items-center gap-2 text-sm text-muted-foreground'>
              {materialLabel}
              {requiresCondition && <ConditionBadge condition={requiresCondition} />}
            </p>
          )}
        </DialogHeader>

        <Section icon={Package} title='What arrived' step={1}>
          <div className='grid gap-3 sm:grid-cols-2'>
            <Field label='How many' required>
              <Input
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                type='number'
                min={1}
                placeholder='0'
                autoFocus
              />
            </Field>
            <Field label='Expiry date *'>
              <Input
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                type='date'
                required
              />
            </Field>
          </div>
          <Field label='Vendor lot number'>
            <Input
              value={vendorLotNumber}
              onChange={(e) => setVendorLotNumber(e.target.value)}
              placeholder='Optional'
            />
          </Field>
        </Section>

        <Section icon={MapPin} title='Where it went' step={2}>
          {slot ? (
            // The caller picked the slot — this is a receipt INTO it, so the
            // destination is stated, not asked again.
            <div className='rounded-md border bg-muted/30 px-3 py-2'>
              <p className='text-sm font-medium'>{locate(slot.path)}</p>
              {/* No condition badge here. `requiresCondition` is what the
                  MATERIAL must be kept at, not what this slot provides, and
                  under "Straight into this slot" it reads as a property of the
                  destination — wrong in exactly the case that matters, when the
                  two differ. It is already stated beside the material above. */}
              <p className='mt-0.5 text-xs text-muted-foreground'>Straight into this slot</p>
            </div>
          ) : (
            <>
              <div className='grid gap-3 sm:grid-cols-2'>
            <Field label='Storage unit'>
              <Select
                value={unitId || 'NONE'}
                onValueChange={(v) => {
                  setUnitId(v === 'NONE' ? '' : v)
                  setSlotId('')
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='NONE'>Not put away yet</SelectItem>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name ?? u.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {unitId && (
              <Field label='Slot'>
                <Select
                  value={slotId}
                  onValueChange={setSlotId}
                  disabled={free.isLoading || freeSlots.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        free.isLoading
                          ? 'Loading…'
                          : freeSlots.length === 0
                            ? 'No free slots'
                            : `Choose from ${freeTotal.toLocaleString()} free…`
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {freeSlots.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {locate(s.path)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>

          {chosenUnit && (
            <p className='flex flex-wrap items-center gap-2 text-xs text-muted-foreground'>
              <ConditionBadge condition={chosenUnit.providesCondition} />
              {freeTotal > 0 && (
                <span>
                  {freeTotal.toLocaleString()} free slot{freeTotal === 1 ? '' : 's'}
                  {free.data?.truncated && ` · the first ${freeSlots.length.toLocaleString()} are listed`}
                </span>
              )}
            </p>
          )}
          {/* Says what the list is showing, and hovers to say which units are
              out and why — "where is my freezer" has to be answerable. */}
          {narrowing && (
            <p
              className='text-xs text-muted-foreground'
              title={hiddenUnits
                .map((h) => `${h.unit.name ?? h.unit.code}: ${h.reason}`)
                .join('\n')}
            >
              {narrowing}
            </p>
          )}
          {requiresCondition && units.length === 0 && (
            <p className='text-xs text-amber-700'>
              No unit is operated at {conditionLabel(requiresCondition, conditionMeta)}. Receive it
              without a slot for now — it shows as unplaced until there is somewhere to put it.
            </p>
          )}
          {!unitId && (
            <p className='text-xs text-muted-foreground'>
              Received stock with no slot yet is fine — it shows as unplaced until someone shelves
              it.
            </p>
          )}
          {unitId && free.isError && (
            <p className='text-xs text-destructive'>
              Could not load this unit's slots: {(free.error as Error)?.message ?? 'unknown error'}
            </p>
          )}
          {unitId && free.data && freeSlots.length === 0 && (
            <p className='text-xs text-amber-700'>
              {free.data.declared === 0
                ? "No slots are described in this unit yet. Receive it without a slot, or declare the unit's layout first."
                : 'This unit is full. Pick another, or receive it without a slot for now.'}
            </p>
          )}
            </>
          )}
        </Section>

        {error && <p className='text-sm text-destructive'>{error}</p>}

        <div className='flex items-center justify-between gap-2 pt-1'>
          <p className='text-xs text-muted-foreground'>
            {!qtyValid
              ? ' '
              : slot
                ? `${quantity} into ${locate(slot.path)}`
                : slotId
                  ? `${quantity} into ${locate(freeSlots.find((s) => s.id === slotId)?.path ?? '')}`
                  : `${quantity}, not put away`}
          </p>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={addLot.isPending || !qtyValid || !expiry}
              className='gap-2'
            >
              <Check className='h-4 w-4' />
              {addLot.isPending ? 'Adding…' : 'Add stock'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <RepeatVendorLotDialog
      vendorLotNumber={vendorLotNumber.trim()}
      receipts={repeatOf}
      pending={addLot.isPending}
      onCancel={() => setRepeatOf(null)}
      onConfirm={() => {
        setRepeatOf(null)
        save()
      }}
    />
    </>
  )
}

/**
 * "You have had this supplier lot before — is that what you meant?"
 *
 * States what is already on file rather than asking a bare yes/no, because the
 * question is only answerable against it: seeing the earlier receipts is how an
 * operator tells a genuine second shipment from the same stock keyed twice.
 *
 * It does not name the number the receipt will get. That is minted by the
 * server at save time, and guessing it here would mean keeping a copy of the
 * minting rule in the client, which would be wrong the first time either side
 * changed.
 */
function RepeatVendorLotDialog({
  vendorLotNumber,
  receipts,
  pending,
  onCancel,
  onConfirm,
}: {
  vendorLotNumber: string
  receipts: ApiLot[] | null
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <Dialog open={receipts !== null} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader className='space-y-1'>
          <DialogTitle className='flex items-center gap-2 text-base'>
            <AlertTriangle className='h-4 w-4 text-amber-600' />
            This vendor lot is already here
          </DialogTitle>
        </DialogHeader>

        <p className='text-sm text-muted-foreground'>
          Vendor lot <span className='font-medium text-foreground'>{vendorLotNumber}</span> has
          already been received for this material
          {receipts && receipts.length > 1 ? `, ${receipts.length} times` : ''}:
        </p>

        <ul className='space-y-1 rounded-md border bg-muted/30 px-3 py-2'>
          {(receipts ?? []).map((l) => (
            <li key={l.id} className='flex justify-between gap-3 text-sm'>
              <span className='font-medium'>{l.lotNumber}</span>
              <span className='text-muted-foreground'>
                {l.quantity}
                {l.expiryDate ? ` · exp ${l.expiryDate.slice(0, 10)}` : ''}
              </span>
            </li>
          ))}
        </ul>

        <p className='text-sm text-muted-foreground'>
          Saving records another receipt of that same lot — it joins the family above with the next
          receipt number, rather than starting a new lot.
        </p>

        <div className='flex justify-end gap-2 pt-1'>
          <Button variant='outline' onClick={onCancel}>
            Go back
          </Button>
          <Button onClick={onConfirm} disabled={pending} className='gap-2'>
            <Check className='h-4 w-4' />
            {pending ? 'Adding…' : 'Yes, receive it'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Section({
  icon: Icon,
  title,
  step,
  children,
}: {
  icon: React.ElementType
  title: string
  step: number
  children: React.ReactNode
}) {
  return (
    <section className='space-y-3'>
      <h3 className='flex items-center gap-2 text-sm font-medium text-gray-900'>
        <span className='flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground'>
          {step}
        </span>
        <Icon className='h-4 w-4 text-muted-foreground' />
        {title}
      </h3>
      <div className='space-y-3 border-l pl-4'>{children}</div>
    </section>
  )
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string
  hint?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div className='space-y-1'>
      <Label className={cn(required && "after:ml-0.5 after:text-destructive after:content-['*']")}>
        {label}
      </Label>
      {children}
      {hint && <p className='text-[11px] leading-relaxed text-muted-foreground'>{hint}</p>}
    </div>
  )
}
